import React, { useState, useEffect, useCallback } from 'react';

function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]');
  return el ? el.content : '';
}

async function postForm(url, entries) {
  const params = new URLSearchParams();
  Object.entries(entries).forEach(([k, v]) => params.append(k, v));
  return fetch(url, {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken() },
    credentials: 'same-origin',
    body: params,
  });
}

// @context TGW-1769 チャンネル設定モーダル。名前/説明の編集・非公開メンバー管理・アーカイブ。
//   並び替え(position)は要件で不採用のため出さない。メンバー一覧は既存
//   /api/message/message_groups_users を流用する。
// @context TGW-2018 公開→非公開の切替を追加（一方向のみ。逆方向はAPI側で拒否）。
export default function ChannelSettingsModal({
  circleId,
  channel,
  onClose,
  onChanged,
}) {
  const [name, setName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description || '');
  const [members, setMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadMembers = useCallback(async () => {
    const res = await fetch(
      `/api/message/message_groups_users?message_group_id=${channel.id}`,
      {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      },
    );
    if (res.ok) {
      const data = await res.json();
      const list = data.message_groups_users || data.messageGroupsUsers || [];
      setMembers(
        list
          .map((row) => ({
            id: row.user ? row.user.id : row.user_id,
            name: row.user
              ? `${row.user.familyname || ''} ${row.user.firstname || ''}`.trim()
              : `user:${row.user_id}`,
          }))
          .filter((m) => m.id),
      );
    }
  }, [channel.id]);

  useEffect(() => {
    if (channel.isPrivate) loadMembers();
  }, [channel.isPrivate, loadMembers]);

  useEffect(() => {
    if (!channel.isPrivate) return undefined;
    const timer = setTimeout(async () => {
      const res = await fetch(
        `/api/message/mention_candidates?circle_id=${circleId}&q=${encodeURIComponent(memberQuery)}`,
        { credentials: 'same-origin', headers: { Accept: 'application/json' } },
      );
      if (res.ok) {
        const data = await res.json();
        setCandidates(data.candidates || []);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [channel.isPrivate, memberQuery, circleId]);

  const save = async () => {
    if (!name.trim()) {
      setError('チャンネル名を入力してください');
      return;
    }
    setSubmitting(true);
    const res = await postForm('/api/channels/update', {
      message_group_id: channel.id,
      channel_name: name.trim(),
      description,
    });
    setSubmitting(false);
    if (res.ok) {
      onChanged({ archived: false });
    } else {
      setError('更新に失敗しました');
    }
  };

  const archive = async () => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `# ${channel.name} をアーカイブしますか？\nチャンネルは一覧から見えなくなります。`,
      )
    )
      return;
    const res = await postForm('/api/channels/archive', {
      message_group_id: channel.id,
      archived: 'true',
    });
    if (res.ok) {
      onChanged({ archived: true });
    } else {
      setError('アーカイブに失敗しました');
    }
  };

  // @context TGW-2018 公開→非公開の一方向切替。不可逆な操作のため confirm で
  //   「メンバー以外に見えなくなる・招待制になる・戻せない」を明示してから送信する。
  const makePrivate = async () => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `# ${channel.name} を非公開チャンネルにしますか？\n現在のメンバー以外には見えなくなり、以後は招待制になります。\n非公開にした後、公開には戻せません。`,
      )
    )
      return;
    const res = await postForm('/api/channels/update', {
      message_group_id: channel.id,
      is_private: 'true',
    });
    if (res.ok) {
      onChanged({ archived: false });
    } else {
      setError('更新に失敗しました');
    }
  };

  const addMember = async (candidate) => {
    const res = await postForm('/api/channels/add_member', {
      message_group_id: channel.id,
      user_id: candidate.id,
    });
    if (res.ok) loadMembers();
  };

  const removeMember = async (member) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${member.name} さんをこのチャンネルから外しますか？`))
      return;
    const res = await postForm('/api/channels/remove_member', {
      message_group_id: channel.id,
      user_id: member.id,
    });
    if (res.ok) loadMembers();
  };

  return (
    <div className="slackch-modal-overlay" onClick={onClose}>
      <div className="slackch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="slackch-modal-header">
          {channel.isPrivate ? '🔒' : '#'} {channel.name} の設定
          <button
            type="button"
            className="slackch-modal-close"
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
        <div className="slackch-modal-body">
          {error && <div className="slackch-modal-error">{error}</div>}

          <label
            className="slackch-modal-label"
            htmlFor="slackch-settings-name"
          >
            チャンネル名
          </label>
          <input
            id="slackch-settings-name"
            className="slackch-modal-input"
            type="text"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />

          <label
            className="slackch-modal-label"
            htmlFor="slackch-settings-desc"
          >
            説明
          </label>
          <input
            id="slackch-settings-desc"
            className="slackch-modal-input"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {!channel.isPrivate && (
            <div className="slackch-modal-members">
              <div className="slackch-modal-label">公開設定</div>
              <button
                type="button"
                className="slackch-btn-ghost"
                onClick={makePrivate}
              >
                🔒 非公開チャンネルにする
              </button>
            </div>
          )}

          {channel.isPrivate && (
            <div className="slackch-modal-members">
              <div className="slackch-modal-label">
                メンバー（非公開チャンネル）
              </div>
              <div className="slackch-modal-chips">
                {members.map((m) => (
                  <span key={m.id} className="slackch-modal-chip">
                    {m.name}
                    <button
                      type="button"
                      onClick={() => removeMember(m)}
                      aria-label={`${m.name}を外す`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              <input
                className="slackch-modal-input"
                type="text"
                value={memberQuery}
                placeholder="名前で検索して招待"
                onChange={(e) => setMemberQuery(e.target.value)}
              />
              <div className="slackch-modal-candidates">
                {candidates
                  .filter((c) => !members.some((m) => m.id === c.id))
                  .slice(0, 8)
                  .map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => addMember(c)}
                    >
                      {c.avatar_thumb ? (
                        <img src={c.avatar_thumb} alt="" />
                      ) : null}
                      {c.name}
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div className="slackch-modal-actions slackch-modal-actions--split">
            <button
              type="button"
              className="slackch-btn-danger"
              onClick={archive}
            >
              アーカイブする
            </button>
            <span>
              <button
                type="button"
                className="slackch-btn-ghost"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="slackch-btn-primary"
                onClick={save}
                disabled={submitting}
              >
                保存
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
